
import { Component, Emit, Mixins, Model, Prop } from 'vue-property-decorator';

import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { Clip, ClipProvider, ObjectType, SraEventAction } from '@/@types/graphql-types';
import { YoutubePlayerOptions } from '@/interfaces/components/youtube-player';

import YoutubePlayerGraphql from '@/ui-components/YoutubePlayerGraphql.vue';
import { ClipsEvent } from './types';
import { SponsoredAdFragmentFragment } from '@/pages/graphql/fragments/SponsoredAd.fragment';
import BackendTrackingMixin from '@/mixins/BackendTracking.vue';

@Component({
	name: 'TitleClips',
	components: { YoutubePlayerGraphql },
})
export default class TitleClips extends Mixins(BackendTrackingMixin) {
	@Prop({ required: true }) clips: Clip[];
	@Prop({ default: 4, type: Number }) limit: number;
	// Whether data is still fetching and blocking elements need to be displayed
	@Prop({ default: false }) isBlocking: boolean;
	@Prop({ default: null, type: String }) titleObjectType: ObjectType;
	@Prop({ default: null, type: Number }) titleObjectId: number;
	@Prop({ default: null, type: String }) titleId: string;
	@Prop({ default: null, type: Number }) seasonNumber: number;
	@Prop({ default: null, type: Number }) episodeNumber: number;
	@Prop({ default: false }) mute?: boolean;
	//The number of simultaneous autoplaying clips is limited to one
	@Prop({ default: false }) autoplay?: boolean;
	@Prop({ default: '' }) source: string;
	@Prop({ default: () => [] }) additionalContexts: SnowplowContext[];
	@Prop({ default: null }) influencedSponsoredAd: SponsoredAdFragmentFragment;

	@Model(ClipsEvent.MODEL, { default: null }) readonly activeClipId!: string | null;

	trackingMap: any = {
		buffering: 'youtube_buffering',
		playing: 'youtube_playing',
		paused: 'youtube_paused',
	};
	trackingProviders: Record<ClipProvider | 'PLEX', number> = {
		[ClipProvider.Youtube]: 1,
		[ClipProvider.Videobuster]: 2,
		[ClipProvider.Dailymotion]: 3,
		PLEX: 4,
	};
	hasPlayedClips: Record<string, boolean> = {};

	get visibleClips() {
		return this.clips.slice(0, this.limit);
	}

	get hasAutoplay() {
		return this.limit === 1 && this.autoplay === true;
	}

	get playerVars(): YoutubePlayerOptions {
		return {
			autoplay: this.hasAutoplay ? 1 : 0,
			mute: this.mute ? 1 : 0,
		};
	}

	isActiveClip(clip: Clip) {
		const id = this.activeClipId?.split(/:(.*)/s); // to split into only array of two strings.
		if (!id) return false;

		return id[0] === this.source && id[1] == clip.externalId;
	}

	onActivate(clipId: string | null) {
		const id = clipId ? `${this.source}:${clipId}` : null;

		this.emitTrailerPlayingModel(id);
	}

	@Emit(ClipsEvent.MODEL)
	emitTrailerPlayingModel(activeClipId: string | null) {
		return activeClipId;
	}

	onTrack(eventName: string, clip: Clip) {
		const category = this.trackingMap[eventName];
		if (category) {
			TrackingHelper.trackEvent(
				category,
				{
					action: this.titleObjectType.toLowerCase(),
					label: this.source,
					nonInteraction: this.autoplay,
					value: this.trackingProviders[clip.provider],
				},
				this.additionalContexts
			);
		}

		// track youtube_started only once if the clip is played at least once
		if (eventName === 'playing') {
			// track influenced campaign metrics
			const influenced = !!this.influencedSponsoredAd;
			if (influenced) {
				this.$backendTracking.trackSraEvent(
					SraEventAction.PlayTrailerClicked,
					this.influencedSponsoredAd,
					this.titleId
				);
			}

			if (!(clip.externalId in this.hasPlayedClips)) {
				TrackingHelper.trackEvent(
					'youtube_started',
					{
						action: this.titleObjectType.toLowerCase(),
						label: this.source,
						nonInteraction: true,
						value: this.trackingProviders[clip.provider],
					},
					this.additionalContexts
				);
			}
			this.hasPlayedClips[clip.externalId] = true;
		}
		// for TrailerAutoplay module
		if (eventName === 'ended') {
			this.$emit('ended');
		}
	}
}
